import { tracking } from 'App/Tracking';
import { EventName } from '__generated__/globalTypes';
import * as braze from '@braze/web-sdk';
import * as Sentry from '@sentry/browser';
import { Events } from './Events';
import { trackServerside } from './serverside/trackServerSide';

export const EventNameServerSideMap: Partial<Record<Events, EventName>> = {
  'shared-content-viewed': EventName.SHARED_CONTENT_VIEWED,
};

/**
 * Supported destinations for event logging.
 * - 'amplitude': client-side analytics
 * - 'braze': Braze marketing/engagement platform
 * - 'core-api': server-side tracking
 */
export type Destination = 'amplitude' | 'braze' | 'core-api';

/**
 * Mapping of events to the destinations they should be sent to.
 * Multiple destinations are supported.
 */
export const EventNameDestinationMap: Partial<Record<Events, Destination[]>> = {
  'shared-content-viewed': ['braze', 'core-api', 'amplitude'],
  'index-submitted': ['amplitude', 'braze'],
  'ai-coach-chatbot-message-sent': ['amplitude', 'braze'],
  'ai-coach-chatbot-url-message-sent': ['amplitude', 'braze'],
  'course-session-completed': ['amplitude', 'braze'],
  'course-segment-completed': ['amplitude', 'braze'],
  'course-completed': ['amplitude', 'braze'],
  'talk-location-set': ['braze'],
  'nova-url-clicked': ['amplitude', 'braze'],
  'content-selected': ['amplitude', 'braze'],
  'wellbeing-measure-started': ['amplitude', 'braze'],
  'wellbeing-measure-completed': ['amplitude', 'braze'],
};

/**
 * Parameters for the customBrazeLogger function.
 */
interface CustomBrazeLoggerParams {
  /** The name of the event to log */
  eventName: Events;
  /** Arbitrary key-value properties to attach to the event */
  properties?: Record<string, unknown>;
}

/**
 * Multi-destination event logger that routes events to the correct destinations.
 *
 * This function will:
 * 1. Determine which destinations are configured for the event.
 * 2. Send the event to each destination:
 * - `braze` → uses Braze Web SDK
 * - `amplitude` → uses client-side tracking
 * - `core-api` → sends to the server via trackServerside
 * 3. Log warnings for any unknown destinations.
 *
 * @param eventName - The event to log
 * @param properties - Key-value properties for the event
 */
export const multiDestinationLogger = async ({
  eventName,
  properties,
}: CustomBrazeLoggerParams) => {
  const destinations = EventNameDestinationMap[eventName] ?? [];
  const serverSideEventName = EventNameServerSideMap[eventName];

  if (destinations.includes('braze')) {
    try {
      braze.logCustomEvent(eventName, properties);
    } catch (err) {
      Sentry.captureException(err);
    }
  }

  if (destinations.includes('core-api')) {
    if (!serverSideEventName) {
      Sentry.captureException(
        new Error(`No server-side event name mapped for: ${eventName}`),
      );
    } else {
      try {
        await trackServerside({
          eventName: serverSideEventName,
          eventProperties: properties,
        });
      } catch (err) {
        Sentry.captureException(err);
      }
    }
  }

  if (destinations.includes('amplitude')) {
    try {
      tracking.track(eventName, properties);
    } catch (err) {
      Sentry.captureException(err);
    }
  }
};
